import { useEffect, useRef, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { Building2, ChevronDown } from 'lucide-preact';
import type { Unit, ClientUnit } from '../../types/api';
import { Card, LoadingState } from '../../components/ui';
import { MapSection } from '../../components/overview';
import { useFilter } from '../../contexts/FilterContext';
import type { LocationData } from '../overview/types';

interface UnitPropertyHeaderProps {
  unitDetails: Unit | null;
  loading: boolean;
  mapLocations: LocationData[];
}

function getUnitDisplayName(unit: ClientUnit) {
  return `${unit.country || 'Unknown'} - Unit ${unit.unit_id_no}`;
}

function UnitFilterSelect() {
  const { selectedUnit, clientUnits, loading, setUnit } = useFilter();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const handleSelect = (unit: ClientUnit) => {
    setOpen(false);
    setUnit(unit);
  };

  return (
    <div className="unit-property__filter" ref={containerRef}>
      <button
        type="button"
        className={`unit-property__filter-button${open ? ' is-open' : ''}`}
        onClick={() => setOpen((value) => !value)}
        disabled={loading || clientUnits.length === 0}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="unit-property__filter-text">
          {selectedUnit ? getUnitDisplayName(selectedUnit) : 'Select Unit'}
        </span>
        <ChevronDown size={14} className="unit-property__filter-chevron" aria-hidden="true" />
      </button>

      {open && clientUnits.length > 0 && (
        <div className="unit-property__filter-menu" role="listbox">
          {clientUnits.map((unit) => (
            <button
              key={unit.unit_id_no}
              type="button"
              role="option"
              aria-selected={selectedUnit?.unit_id_no === unit.unit_id_no}
              className={`unit-property__filter-item${
                selectedUnit?.unit_id_no === unit.unit_id_no ? ' is-selected' : ''
              }`}
              onClick={() => handleSelect(unit)}
            >
              {getUnitDisplayName(unit)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ComponentChildren }) {
  return (
    <div className="unit-property__field">
      <span className="unit-property__field-label">{label}</span>
      <div className="unit-property__field-value">{children}</div>
    </div>
  );
}

export function UnitPropertyHeader({
  unitDetails,
  loading,
  mapLocations,
}: UnitPropertyHeaderProps) {
  return (
    <Card as="section" padding="flush" className="unit-property">
      <div className="unit-property__head">
        <div className="unit-property__title-group">
          <span className="unit-property__title-icon" aria-hidden="true">
            <Building2 size={15} strokeWidth={2} />
          </span>
          <div className="unit-property__title-text">
            <h2 className="unit-property__title">Property Overview</h2>
            {unitDetails?.unit_type && (
              <span className="unit-property__type">{unitDetails.unit_type}</span>
            )}
          </div>
        </div>
        <UnitFilterSelect />
      </div>

      {loading ? (
        <LoadingState message="Loading property details…" className="unit-property__loading" />
      ) : unitDetails ? (
        <div className="unit-property__content">
          <div className="unit-property__details">
            <Field label="Address">
              {unitDetails.address || `Unit ${unitDetails.unit_id_no}`}
            </Field>
            <Field label="Remarks">{unitDetails.remark || '—'}</Field>
            <div className="unit-property__meta">
              <Field label="Currency">{unitDetails.currency || '—'}</Field>
              <Field label="Operating Model">
                {unitDetails.operating_model ? (
                  <span className="unit-property__badge">{unitDetails.operating_model}</span>
                ) : (
                  '—'
                )}
              </Field>
            </div>
          </div>

          <div className="unit-property__map">
            <MapSection locations={mapLocations} />
          </div>
        </div>
      ) : null}
    </Card>
  );
}
